import { ArrowRight, Camera, Layers, MapPinned, Mountain, MonitorPlay, Sparkles } from "lucide-react";
import { ButtonLink, Card } from "@/components/play";
import { authLink } from "@/components/onboarding/authContinuation";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { SitePage } from "@/components/SitePage";

/** The home page's three steps, so About tells the same story (pages/LandingPage.tsx). */
const steps = [
  { icon: Camera, title: "Bring your cameras.", text: "Invite cameras with a link or QR code." },
  { icon: MapPinned, title: "See the whole picture.", text: "Find a camera on the map. Tap to watch." },
  { icon: MonitorPlay, title: "Make it your show.", text: "Mix in OBS. Bring your show back to the event." },
];

const values = [
  { icon: Sparkles, title: "Simple first", description: "Start with a name. Invite a camera with a link. We keep the rest out of your way." },
  { icon: Layers, title: "One event. Many experiences.", description: "Every camera lands on the event’s live map. Each host makes their own show on top." },
  { icon: Mountain, title: "Built for endurance", description: "Trail and ultra, gravel and MTB, road running and triathlon." },
];

/** About (/about): what StreamBridge is for, in the brand voice (docs/strategy/brand-voice.md). */
export function AboutPage() {
  useDocumentTitle("About · StreamBridge");
  return (
    <SitePage mainId="about-main" className="text-neutral-10">
      <section className="px-6 py-24">
        <div className="mx-auto max-w-4xl text-center">
          <p className="mb-6 text-size-xs font-medium text-neutral-7">About StreamBridge</p>
          <h1 className="mb-6 text-4xl font-black sm:text-5xl">The home for endurance sports.</h1>
          <p className="mx-auto max-w-2xl text-lg text-neutral-7">
            Host, share and experience endurance sports together. Create an event, bring cameras from the course and
            share the race with your people.
          </p>
        </div>
      </section>

      <section className="px-6 py-20" aria-labelledby="about-view-title">
        <div className="mx-auto max-w-3xl">
          <p className="mb-4 text-size-xs font-medium text-neutral-7">Our point of view</p>
          <h2 id="about-view-title" className="mb-6 text-3xl font-black sm:text-4xl">The best views are human.</h2>
          <div className="space-y-4 text-neutral-8">
            <p>The front runners. The back markers. Your person. The people out on the course know where the moments are.</p>
            <p>Cameras, the map, and your live show, together. Dot watching just became a lot more fun.</p>
          </div>
        </div>
      </section>

      <section className="px-6 py-20" aria-labelledby="about-values-title">
        <div className="mx-auto max-w-6xl">
          <h2 id="about-values-title" className="mb-12 text-center text-3xl font-black sm:text-4xl">What drives us</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {values.map((value) => (
              <Card key={value.title} title={value.title} description={value.description} icon={<value.icon />} headingLevel={3} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20" aria-labelledby="about-steps-title">
        <div className="mx-auto max-w-4xl rounded-2xl bg-neutral-2 p-8 text-center sm:p-12">
          <h2 id="about-steps-title" className="mb-8 text-2xl font-black sm:text-3xl">How it works</h2>
          <ol className="mb-8 grid gap-6 text-left sm:grid-cols-3">
            {steps.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-neutral-1" aria-hidden="true">
                  <Icon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-medium">{title}</span>
                  <span className="block text-sm text-neutral-7">{text}</span>
                </span>
              </li>
            ))}
          </ol>
          <ButtonLink to={authLink("/signup", "/app?create=1")}>
            Create event
            <ArrowRight aria-hidden="true" />
          </ButtonLink>
        </div>
      </section>

      <section className="px-6 py-20" aria-labelledby="about-contact-title">
        <div className="mx-auto max-w-4xl text-center">
          <h2 id="about-contact-title" className="mb-4 text-2xl font-black sm:text-3xl">Have questions?</h2>
          <p className="mb-6 text-neutral-7">Read the FAQ or get in touch.</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <ButtonLink to="/faq" variant="secondary">
              FAQ
              <ArrowRight aria-hidden="true" />
            </ButtonLink>
            <ButtonLink href="mailto:hello@streambridge.live" variant="secondary">
              Contact us
            </ButtonLink>
          </div>
        </div>
      </section>
    </SitePage>
  );
}
